import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Typography } from 'src/latest/src/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import AppButton from 'src/components/base/Button/Button';
import { Spinner } from 'react-bootstrap';
import pauseIcon from '../../../../../assets/icons/pauseIcon.svg';
import {
  formatDateMMDDYYYY,
  formatPatientBalance,
  formatToHHMMSS,
  getBalanceColorClass,
  getDaysDifference,
} from 'src/legacy/utils/helpers';
import {
  BillingDashboardTabName,
  dataKey,
  defaultBalanceAgeByAMD,
  DirectoryRow,
  filterKey,
  PatientRow,
  TableData,
  Transaction,
} from './BIllingAdminConstant';
import diputeChargeIcon from '../../../../../assets/icons/icon-three-dots.svg';
import { noop } from 'lodash';

interface BillingAdminHomeTableProps {
  headerData: TableData[];
  loading: boolean;
  activeTab: BillingDashboardTabName;
  isDirectoryPlatform: boolean;
  onSort: (key: string) => void;
  noDataFound: boolean;

  // Data specific props
  patientData?: PatientRow[];
  directoryPatientData?: DirectoryRow[];
  transactionData?: Transaction[];

  // State specific props
  chargingPatientId: string | null;
  searchIds: string[] | null;

  // Handlers
  handlePatientDetailClick: (patient: PatientRow) => void;
  handleDirectoryPatientDetailClick: (patient: DirectoryRow) => void;
  handleChargeClick: (patient: PatientRow) => void;
  handleDirectoryChargeClick: (patient: DirectoryRow) => void;
  handleRefundClick: (transaction: Transaction) => void;

  // Additional info
  totalCount: number;
  fromPatientDetailsPage?: boolean;
  handleDisputeClick?: (transaction: Transaction) => void;
  fromAccountInformationPage?: boolean;
}

const BillingAdminHomeTable: React.FC<BillingAdminHomeTableProps> = ({
  headerData,
  loading,
  activeTab,
  isDirectoryPlatform,
  onSort,
  noDataFound,
  patientData,
  directoryPatientData,
  transactionData,
  chargingPatientId,
  handlePatientDetailClick,
  handleDirectoryPatientDetailClick,
  handleChargeClick,
  handleDirectoryChargeClick,
  handleRefundClick,
  fromPatientDetailsPage = false,
  handleDisputeClick = noop,
  fromAccountInformationPage = false,
}) => {
  const [openDisputeRow, setOpenDisputeRow] = useState<string | null>(null);

  useEffect(() => {
    const closeOnOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (
        !target.closest(
          '.billing-admin-dashboard-patient-data-charge-dispute',
        ) &&
        !target.closest('.dispute-text')
      ) {
        setOpenDisputeRow(null);
      }
    };

    document.addEventListener('click', closeOnOutsideClick);

    return () => document.removeEventListener('click', closeOnOutsideClick);
  }, []);

  const handleDisputeIconClick = (
    e: React.MouseEvent,
    transaction: Transaction,
  ) => {
    e.stopPropagation();
    setOpenDisputeRow(
      openDisputeRow === transaction.id ? null : transaction.id,
    );
  };

  // Render patient row for non-directory platform
  const renderPatientRow = (patient: PatientRow) => (
    <tr key={patient.client_id}>
      <td
        className="billing-admin-dashboard-patient-data"
        onClick={() => handlePatientDetailClick(patient)}
      >
        <Link
          to={`/billing-admin/client-details/${patient.client_id}`}
          state={{ isDirectoryPlatform, data: patient }}
        >
          {patient?.patient_name || <>-</>}
        </Link>
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handlePatientDetailClick(patient)}
      >
        <p
          className={`billing-admin-dashboard-patient-data-dollar ${getBalanceColorClass(
            patient?.patient_balance,
          )}`}
        >
          {formatPatientBalance(patient?.patient_balance)}
        </p>
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handlePatientDetailClick(patient)}
      >
        {patient?.payment_plan === filterKey?.ACTIVE ? (
          <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#027A44' }} />
        ) : patient?.payment_plan === filterKey?.FAILED ? (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={{ color: '#E8403D' }}
          />
        ) : patient?.payment_plan === filterKey?.PAUSED ? (
          <img
            src={pauseIcon}
            className="billing-admin-dashboard-paused-icon"
            alt="pause icon"
          />
        ) : (
          <>-</>
        )}
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handlePatientDetailClick(patient)}
      >
        {patient?.card_status && patient?.card_status === filterKey.NO_CARD ? (
          <div className="billing-admin-dashboard-patient-data-card no-card">
            No card
          </div>
        ) : patient?.card_status === filterKey.VALID_CARD ? (
          <div className="billing-admin-dashboard-patient-data-card active-card">
            Active
          </div>
        ) : patient?.card_status === filterKey.CONSENT_PENDING ? (
          <div className="billing-admin-dashboard-patient-data-card pending-card">
            Pending
          </div>
        ) : patient?.card_status === filterKey.EXPIRED_CARD ? (
          <div className="billing-admin-dashboard-patient-data-card declined-card">
            Expired
          </div>
        ) : (
          <div className="billing-admin-dashboard-patient-data-card declined-card">
            Declined
          </div>
        )}
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handlePatientDetailClick(patient)}
      >
        {patient?.last_charge_at === defaultBalanceAgeByAMD
          ? 'N/A'
          : getDaysDifference(patient?.last_charge_at)}
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handlePatientDetailClick(patient)}
      >
        {patient?.next_appointment_at
          ? formatDateMMDDYYYY(patient?.next_appointment_at)
          : '-'}
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        <div className="billing-admin-dashboard-patient-data-charge">
          <AppButton
            label="Charge"
            className="billing-admin-dashboard-patient-data-charge-btn btn-new-primary"
            textSize="size-14"
            textWeight="bold"
            disabled={
              patient?.card_status !== filterKey.VALID_CARD ||
              chargingPatientId !== null
            }
            isProcessing={chargingPatientId === patient.client_id}
            onClick={() => handleChargeClick(patient)}
          />
        </div>
      </td>
    </tr>
  );

  // Render directory patient row
  const renderDirectoryPatientRow = (patient: DirectoryRow) => (
    <tr key={patient.provider_id}>
      <td
        className="billing-admin-dashboard-patient-data"
        onClick={() => handleDirectoryPatientDetailClick(patient)}
      >
        <Link
          to={`/billing-admin/client-details/${patient.provider_id}`}
          state={{ isDirectoryPlatform, data: patient }}
        >
          {patient?.provider_name || <>-</>}
        </Link>
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handleDirectoryPatientDetailClick(patient)}
      >
        <p
          className={`billing-admin-dashboard-patient-data-dollar ${getBalanceColorClass(
            patient?.provider_balance,
          )}`}
        >
          {formatPatientBalance(patient?.provider_balance)}
        </p>
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handleDirectoryPatientDetailClick(patient)}
      >
        {patient?.payment_plan === filterKey?.ACTIVE ? (
          <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#027A44' }} />
        ) : patient?.payment_plan === filterKey?.FAILED ? (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={{ color: '#E8403D' }}
          />
        ) : patient?.payment_plan === filterKey?.PAUSED ? (
          <img
            src={pauseIcon}
            className="billing-admin-dashboard-paused-icon"
            alt="pause icon"
          />
        ) : (
          <>-</>
        )}
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handleDirectoryPatientDetailClick(patient)}
      >
        {patient?.card_status && patient?.card_status === filterKey.NO_CARD ? (
          <div className="billing-admin-dashboard-patient-data-card no-card">
            No card
          </div>
        ) : patient?.card_status === filterKey.VALID_CARD ? (
          <div className="billing-admin-dashboard-patient-data-card active-card">
            Active
          </div>
        ) : patient?.card_status === filterKey.CONSENT_PENDING ? (
          <div className="billing-admin-dashboard-patient-data-card pending-card">
            Pending
          </div>
        ) : patient?.card_status === filterKey.EXPIRED_CARD ? (
          <div className="billing-admin-dashboard-patient-data-card declined-card">
            Expired
          </div>
        ) : (
          <div className="billing-admin-dashboard-patient-data-card declined-card">
            Declined
          </div>
        )}
      </td>
      <td
        className="billing-admin-dashboard-patient-data text-center"
        onClick={() => handleDirectoryPatientDetailClick(patient)}
      >
        {patient?.last_charge_at === defaultBalanceAgeByAMD
          ? 'N/A'
          : getDaysDifference(patient?.last_charge_at)}
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        <div className="billing-admin-dashboard-patient-data-charge">
          <AppButton
            label="Charge"
            className="billing-admin-dashboard-patient-data-charge-btn btn-new-primary"
            textSize="size-14"
            textWeight="bold"
            disabled={
              patient?.card_status !== filterKey.VALID_CARD ||
              chargingPatientId !== null
            }
            isProcessing={chargingPatientId === patient.provider_id}
            onClick={() => handleDirectoryChargeClick(patient)}
          />
        </div>
      </td>
    </tr>
  );

  // Render transaction row
  const renderTransactionRow = (transaction: Transaction) => (
    <tr key={transaction.id}>
      <td className="billing-admin-dashboard-patient-data">
        {transaction?.stripePaymentIntentId}
      </td>
      <td className="billing-admin-dashboard-patient-data">
        {isDirectoryPlatform
          ? transaction?.providerName
          : transaction?.clientName}
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        <p className={`billing-admin-dashboard-patient-data-dollar`}>
          {formatPatientBalance(transaction?.amount)}
        </p>
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        {transaction?.status && transaction?.status === filterKey?.PENDING ? (
          <div className="billing-admin-dashboard-patient-data-card no-card">
            Pending
          </div>
        ) : transaction?.status === filterKey?.SUCCESS ||
          transaction?.status === filterKey?.REFUNDED ? (
          <div className="billing-admin-dashboard-patient-data-card active-card">
            {transaction?.status === filterKey?.REFUNDED
              ? 'Refunded'
              : 'Success'}
          </div>
        ) : (
          <div className="billing-admin-dashboard-patient-data-card declined-card">
            Failed
          </div>
        )}
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        <div className="d-flex flex-row align-items-center justify-content-left gap-2">
          <div>{formatDateMMDDYYYY(transaction?.createdAt)}</div>
          <div>{formatToHHMMSS(transaction?.createdAt)}</div>
        </div>
      </td>
      {isDirectoryPlatform && (
        <td className="billing-admin-dashboard-patient-data">
          {transaction?.clientName}
        </td>
      )}
      <td className="billing-admin-dashboard-patient-data text-center">
        <div className="billing-admin-dashboard-patient-data-charge">
          <AppButton
            label="Refund"
            className="billing-admin-dashboard-patient-data-charge-btn btn-new-primary"
            textSize="size-14"
            textWeight="bold"
            disabled={transaction?.status !== filterKey.SUCCESS}
            isProcessing={false}
            onClick={() => handleRefundClick(transaction)}
          />
        </div>
      </td>
    </tr>
  );

  const renderDirectoryPatientTransactionRow = (transaction: Transaction) => (
    <tr key={transaction.id}>
      <td className="billing-admin-dashboard-patient-data">
        {transaction?.stripePaymentIntentId}
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        <p className={`billing-admin-dashboard-patient-data-dollar`}>
          {formatPatientBalance(transaction?.amount)}
        </p>
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        {transaction?.status && transaction?.status === filterKey?.PENDING ? (
          <div className="billing-admin-dashboard-patient-data-card no-card">
            Pending
          </div>
        ) : transaction?.status === filterKey?.SUCCESS ||
          transaction?.status === filterKey?.REFUNDED ? (
          <div className="billing-admin-dashboard-patient-data-card active-card">
            {transaction?.status === filterKey?.REFUNDED
              ? 'Refunded'
              : 'Success'}
          </div>
        ) : (
          <div className="billing-admin-dashboard-patient-data-card declined-card">
            Failed
          </div>
        )}
      </td>
      <td className="billing-admin-dashboard-patient-data text-center">
        <div className="d-flex flex-row align-items-center justify-content-left gap-2">
          <div>{formatDateMMDDYYYY(transaction?.createdAt)}</div>
          <div>{formatToHHMMSS(transaction?.createdAt)}</div>
        </div>
      </td>
      {isDirectoryPlatform && (
        <td className="billing-admin-dashboard-patient-data">
          {transaction?.clientName}
        </td>
      )}
      {fromAccountInformationPage ? (
        <td className="billing-admin-dashboard-patient-data text-center">
          <div className="billing-admin-dashboard-patient-data-charge">
            {transaction?.status === filterKey?.SUCCESS && (
              <div
                className="billing-admin-dashboard-patient-data-charge-dispute"
                onClick={e => handleDisputeIconClick(e, transaction)}
              >
                <img src={diputeChargeIcon} alt="Dispute Charge Icon" />
              </div>
            )}

            {openDisputeRow === transaction.id && (
              <div
                onClick={() => handleDisputeClick(transaction)}
                className="dispute-text"
              >
                <span>Dispute Charge</span>
              </div>
            )}
          </div>
        </td>
      ) : (
        <td className="billing-admin-dashboard-patient-data text-center">
          <div className="billing-admin-dashboard-patient-data-charge">
            <AppButton
              label="Refund"
              className="billing-admin-dashboard-patient-data-charge-btn btn-new-primary"
              textSize="size-14"
              textWeight="bold"
              disabled={transaction?.status !== filterKey.SUCCESS}
              isProcessing={false}
              onClick={() => handleRefundClick(transaction)}
            />
          </div>
        </td>
      )}
    </tr>
  );

  // Determine which data and render function to use
  const getDataAndRenderer = useMemo(() => {
    if (activeTab === BillingDashboardTabName.PATIENTS) {
      if (isDirectoryPlatform) {
        return {
          data: directoryPatientData || [],
          renderRow: renderDirectoryPatientRow,
        };
      } else {
        return {
          data: patientData || [],
          renderRow: renderPatientRow,
        };
      }
    } else {
      if (fromPatientDetailsPage) {
        return {
          data: transactionData || [],
          renderRow: renderDirectoryPatientTransactionRow,
        };
      } else {
        return {
          data: transactionData || [],
          renderRow: renderTransactionRow,
        };
      }
    }
  }, [
    activeTab,
    isDirectoryPlatform,
    directoryPatientData,
    patientData,
    transactionData,
    fromPatientDetailsPage,
    openDisputeRow,
    chargingPatientId,
  ]);

  const { data, renderRow } = getDataAndRenderer;

  return (
    <div className="billing-admin-dashboard-patient-list mt-4">
      <div className="billing-admin-dashboard-patient-list-wrapper">
        <table className="billing-admin-dashboard-patient-table">
          <thead className="billing-admin-dashboard-patient-head">
            <tr className="">
              {headerData?.length > 0 &&
                headerData?.map((data: TableData) => {
                  return (
                    <th
                      className={`billing-admin-dashboard-patient-header ${
                        data?.sortable ? 'cursor' : ''
                      }`}
                      key={data?.key}
                      onClick={() => data?.sortable && onSort(data?.key)}
                    >
                      <div
                        className={`d-flex ${
                          data?.key === dataKey?.PATIENT_NAME ||
                          data?.key === dataKey?.TRANSACTION_ID ||
                          data?.key === dataKey?.TRANSACTION_PATIENT_NAME ||
                          data?.key === dataKey?.TRANSACTION_PROVIDER_NAME ||
                          data?.key === dataKey?.TRANSACTION_DATE
                            ? 'justify-content-left'
                            : 'justify-content-center'
                        }`}
                      >
                        <div className="header-label">{data?.label}</div>
                        {data?.sortable && (
                          <div
                            className={`billing-admin-dashboard-patient-order ${
                              data.orderBy?.toLocaleLowerCase() || ''
                            }`}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="loading-tr-height">
                <td colSpan={headerData?.length}>
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      animation="border"
                      role="status"
                      variant="dark"
                      className="mx-auto"
                    />
                  </div>
                </td>
              </tr>
            ) : noDataFound ? (
              <tr className="loading-tr-height">
                <td colSpan={headerData?.length}>
                  <div className="d-flex justify-content-center align-items-center">
                    <Typography variant="size-18" weight="semi-bold">
                      {activeTab === BillingDashboardTabName.PATIENTS
                        ? 'No User Found'
                        : 'No Transaction Data Found'}
                    </Typography>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row: any) => renderRow(row))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BillingAdminHomeTable;
